import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TEMPLATE_STATUS } from '../constants/messageTemplateKeys';
import { useReturnLocation } from './useReturnLocation';
import {
  createMessageTemplate,
  createMessageTemplateForTenants,
  loadMessageTemplateFacets,
  loadMessageTemplates,
  updateMessageTemplate,
  selectTemplatesVariables,
} from '../store/templatesSlice';
import { buildCreateTemplatePayload } from '../lib/buildCreateTemplatePayload';
import { resolveVariableIds } from '../lib/resolveVariableIds';
import { resolveTabKeyByChannel } from '../lib/resolveTabKeyByChannel';
import { buildTemplateValidationErrorsForAttempt } from '../lib/buildTemplateValidationState';
import { scrollToFirstFieldError } from '../lib/scrollToFirstFieldError';
import {
  hasBasicInformationDraftErrors,
  hasBasicInformationErrors,
} from '../ui/create-template/basic-information/lib/buildBasicInformationErrors';

export function useTemplateSave({
  channelKey,
  isEditMode,
  templateId,
  hasTemplateId,
  isSourceDataLoading,
  basicInfoForm,
  editorContent,
  customStyle = '',
  logoUrl,
  checkedDisclosures,
  senderInfo = null,
  onSaveAttempt,
  skipNextBlock,
  tenantSelectionEmpty = false,
  useForTenantsOnSave = false,
  dayOptionalByRow = [],
  canEditLanguage = true,
}) {
  const dispatch = useDispatch();
  const allVariables = useSelector(selectTemplatesVariables);
  const navigate = useNavigate();
  const returnTo = useReturnLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveTemplate = useCallback(
    async (status) => {
      const isPublish = status === TEMPLATE_STATUS.PUBLISHED;
      onSaveAttempt?.({ isPublish });

      const nextErrors = buildTemplateValidationErrorsForAttempt({
        channelKey,
        form: basicInfoForm,
        isPublish,
        dayOptionalByRow,
      });
      const hasErrors =
        (isPublish
          ? hasBasicInformationErrors(nextErrors)
          : hasBasicInformationDraftErrors(nextErrors)) || tenantSelectionEmpty;

      if (hasErrors) scrollToFirstFieldError();
      if (hasErrors || isSubmitting || isSourceDataLoading) return false;

      setIsSubmitting(true);
      try {
        const payload = buildCreateTemplatePayload({
          channel: channelKey,
          status,
          form: basicInfoForm,
          editorContent,
          customStyle,
          logoUrl,
          checkedDisclosures,
          insertedVariableIds: resolveVariableIds(
            editorContent,
            basicInfoForm?.subjects,
            allVariables,
          ),
          senderInfo,
          includeLanguage: canEditLanguage,
        });

        if (!isEditMode && useForTenantsOnSave && !isPublish) {
          const isForAllTenants = Boolean(basicInfoForm?.isForAllTenants);
          const tenantIds = (basicInfoForm?.selectedTenantIds ?? []).map(String);
          const forTenantsPayload = {
            ...payload,
            status: TEMPLATE_STATUS.DRAFT,
            ...(isForAllTenants ? { is_for_all_tenants: true } : { tenant_ids: tenantIds }),
          };
          await dispatch(createMessageTemplateForTenants({ payload: forTenantsPayload })).unwrap();
        } else if (isEditMode && hasTemplateId) {
          await dispatch(updateMessageTemplate({ templateId, payload })).unwrap();
        } else {
          await dispatch(createMessageTemplate({ payload })).unwrap();
        }

        const tabKey = resolveTabKeyByChannel(channelKey);
        await Promise.all([
          dispatch(loadMessageTemplates({ tabKey })),
          dispatch(loadMessageTemplateFacets({ tabKey })),
        ]);

        return true;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      basicInfoForm,
      channelKey,
      checkedDisclosures,
      allVariables,
      dispatch,
      editorContent,
      customStyle,
      logoUrl,
      senderInfo,
      hasTemplateId,
      isSourceDataLoading,
      isEditMode,
      isSubmitting,
      onSaveAttempt,
      templateId,
      tenantSelectionEmpty,
      useForTenantsOnSave,
      dayOptionalByRow,
      canEditLanguage,
    ],
  );

  const submitTemplate = useCallback(
    async (status) => {
      const saved = await saveTemplate(status);
      if (saved) {
        skipNextBlock?.();
        navigate(returnTo);
      }
    },
    [saveTemplate, skipNextBlock, navigate, returnTo],
  );

  const deactivateTemplate = useCallback(async () => {
    if (!hasTemplateId || isSubmitting || isSourceDataLoading) return;
    onSaveAttempt?.({ isPublish: false });

    const nextErrors = buildTemplateValidationErrorsForAttempt({
      channelKey,
      form: basicInfoForm,
      isPublish: false,
      dayOptionalByRow,
    });
    if (hasBasicInformationDraftErrors(nextErrors) || tenantSelectionEmpty) {
      scrollToFirstFieldError();
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildCreateTemplatePayload({
        channel: channelKey,
        status: TEMPLATE_STATUS.INACTIVE,
        form: basicInfoForm,
        editorContent,
        customStyle,
        logoUrl,
        checkedDisclosures,
        insertedVariableIds: resolveVariableIds(
          editorContent,
          basicInfoForm?.subjects,
          allVariables,
        ),
        senderInfo,
        includeLanguage: canEditLanguage,
      });

      await dispatch(updateMessageTemplate({ templateId, payload })).unwrap();

      const tabKey = resolveTabKeyByChannel(channelKey);
      await Promise.all([
        dispatch(loadMessageTemplates({ tabKey })),
        dispatch(loadMessageTemplateFacets({ tabKey })),
      ]);

      skipNextBlock?.();
      navigate(returnTo);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    basicInfoForm,
    channelKey,
    checkedDisclosures,
    allVariables,
    dispatch,
    editorContent,
    customStyle,
    logoUrl,
    senderInfo,
    hasTemplateId,
    isSourceDataLoading,
    isSubmitting,
    navigate,
    onSaveAttempt,
    returnTo,
    skipNextBlock,
    templateId,
    tenantSelectionEmpty,
    dayOptionalByRow,
    canEditLanguage,
  ]);

  return { saveTemplate, submitTemplate, deactivateTemplate, isSubmitting };
}
